import { AnyAction } from 'redux';

import { TabNames } from 'const/check';
import {
  IChangeTabName,
  ICheckCategoryChange,
  ICheckSelection,
  IExternalId,
  IFetchAssessmentPageError,
  IFetchAssessmentPageSuccess,
  IPageChange,
  IScaleChange,
  IToggleBordersVisibility,
  ISetUnitBoxCoordinates,
  IRemoveSSIUnit,
  IAddSSIUnit,
  IUpdateSSIUnitResult,
  IUpdateSSIUnitTextUsed,
  IUpdateSSIUnitTextRequired,
} from 'actions/assessmentPage';
import {
  CHECK_CATEGORY_CHANGE,
  CHECK_SELECTION,
  FETCH_ASSESSMENT_PAGE_ERROR,
  FETCH_ASSESSMENT_PAGE_START,
  FETCH_ASSESSMENT_PAGE_SUCCESS,
  PAGE_CHANGE,
  SCALE_CHANGE,
  SET_EXTERNAL_ID,
  SET_PREV_EXTERNAL_ID,
  SET_QUALITY_CHECK_TABNAME,
  TOGGLE_BORDERS_VISIBILITY,
  SET_UNIT_BOX_COORDINATES,
  REMOVE_SSI_UNIT,
  ADD_SSI_UNIT,
  UPDATE_SSI_UNIT_RESULT,
  UPDATE_SSI_UNIT_TEXT_USED,
  UPDATE_SSI_UNIT_TEXT_REQUIRED,
} from 'actions/assessmentPage/types';
import {
  ReviewResult,
  SplitMode,
  ReviewCharacteristic,
  SuggestionValueKeys,
} from 'types/check';
import { AggregatedAssessmentPage, ChecksAlert } from 'types/check-results';
import { Error } from 'types/error';
import { Review } from 'types/review-category';
import { preparePage } from 'utils/checks';
import { nanoid } from 'nanoid';
import { parseToChecks } from 'pqaAccuracy/dataTransfer/reportImportCache';

export type AssessmentPageReducerType = {
  pages: (AggregatedAssessmentPage | undefined)[];
  currentPage: AggregatedAssessmentPage | null;
  error: Error | null;
  externalId: string | null;
  isBordersVisible: boolean;
  isLoading: boolean;
  prevExternalId: string | null;
  scale: number;
  selectedCategory: Review;
  selectedCheck: string | null;
  selectedPage: number;
  selectedResult: ReviewResult | null;
  selectedTabName: TabNames;
};

export const initialState = (): AssessmentPageReducerType => ({
  pages: [],
  currentPage: null,
  error: null,
  externalId: null,
  isBordersVisible: true,
  isLoading: false,
  prevExternalId: null,
  scale: 1,
  selectedCategory: Review.safety,
  selectedCheck: null,
  selectedPage: 1,
  selectedResult: null,
  selectedTabName: TabNames.safety,
});

function handleFetchStart(state: AssessmentPageReducerType) {
  return {
    ...state,
    isLoading: true,
    currentPage: null,
    error: null,
  };
}

function handleFetchSuccess(
  state: AssessmentPageReducerType,
  action: IFetchAssessmentPageSuccess
) {
  let page = action.cachedPage
    ? action.cachedPage
    : action.currentPage
    ? preparePage(action.currentPage)
    : null;
  if (page) state.pages[state.selectedPage] = page;

  return {
    ...state,
    isLoading: false,
    currentPage: page,
  };
}

function handleFetchError(
  state: AssessmentPageReducerType,
  action: IFetchAssessmentPageError
) {
  return {
    ...state,
    isLoading: false,
    error: { ...action.error },
  };
}

function handleCheckSelection(
  state: AssessmentPageReducerType,
  action: ICheckSelection
) {
  return {
    ...state,
    selectedCheck: action.id,
  };
}

function handleTabNameSelection(
  state: AssessmentPageReducerType,
  action: IChangeTabName
) {
  return {
    ...state,
    selectedTabName: action.tabTitle,
  };
}

function handleSetExternalId(
  state: AssessmentPageReducerType,
  action: IExternalId
) {
  return {
    ...state,
    externalId: action.id,
  };
}

function handleSetPrevExternalId(
  state: AssessmentPageReducerType,
  action: IExternalId
) {
  return {
    ...state,
    prevExternalId: action.id,
  };
}

function handleToggleBordersVisibility(
  state: AssessmentPageReducerType,
  _: IToggleBordersVisibility
) {
  return {
    ...state,
    isBordersVisible: !state.isBordersVisible,
  };
}

function handlePageChange(
  state: AssessmentPageReducerType,
  action: IPageChange
) {
  return {
    ...state,
    isLoading: true,
    currentPage: state.pages[action.pageNumber] ?? null,
    selectedPage: action.pageNumber,
    selectedCheck: null,
    externalId: null,
  };
}

function handleScaleChange(
  state: AssessmentPageReducerType,
  action: IScaleChange
) {
  return {
    ...state,
    scale: action.scale,
  };
}

function handleCheckCategoryChange(
  state: AssessmentPageReducerType,
  action: ICheckCategoryChange
) {
  return {
    ...state,
    selectedCheck: null,
    externalId: null,
    selectedCategory: action.category,
    selectedResult: action.result,
  };
}

function handleSetUnitBoxCoordinates(
  state: AssessmentPageReducerType,
  action: ISetUnitBoxCoordinates
) {
  if (!state.currentPage) return { ...state };
  const category = state.currentPage[state.selectedCategory];
  const page = {
    ...state.currentPage,
    [state.selectedCategory]: {
      ...category,
      groups: category.groups.map(alert =>
        alert.externalId === action.externalId
          ? { ...alert, borderBox: action.newCoordinates }
          : alert
      ),
    },
  };

  state.pages[state.selectedPage] = page;
  return {
    ...state,
    currentPage: page,
  };
}

function handleRemoveSSIUnit(
  state: AssessmentPageReducerType,
  action: IRemoveSSIUnit
) {
  if (!state.currentPage) return { ...state };
  const category = state.currentPage[state.selectedCategory];
  if (!category.groups.find(alert => alert.externalId === action.externalId))
    return { ...state };

  return {
    ...state,
    currentPage: {
      ...state.currentPage,
      [state.selectedCategory]: {
        ...category,
        groups: category.groups.filter(
          alert => alert.externalId !== action.externalId
        ),
      },
    },
  };
}

function handleAddSSIUnit(
  state: AssessmentPageReducerType,
  action: IAddSSIUnit
) {
  if (!state.currentPage) return { ...state };
  const category = state.currentPage[state.selectedCategory];

  return {
    ...state,
    currentPage: {
      ...state.currentPage,
      [state.selectedCategory]: {
        ...category,
        groups: [
          ...category.groups,
          {
            id: nanoid(),
            externalId: nanoid(),
            result: ReviewResult.fail,
            checks: parseToChecks({
              ssiCheck: {
                result: ReviewResult.fail,
                used: 'Default Text',
                required: 'Default Text',
              },
            }),
            splitMode: SplitMode.undivided,
            borderBox: action.newCoordinates,
          },
        ],
      },
    },
  };
}

const moveResult = (result: ReviewResult) => {
  switch (result) {
    case ReviewResult.pass:
      return ReviewResult.warning;
    case ReviewResult.warning:
      return ReviewResult.fail;
    case ReviewResult.fail:
      return ReviewResult.pass;
    default:
      return result;
  }
};

function handleUpdateSSIUnitResult(
  state: AssessmentPageReducerType,
  action: IUpdateSSIUnitResult
) {
  const { externalId, characteristic } = action;

  if (!state.currentPage) return state;
  const category = state.currentPage[state.selectedCategory];
  const alert = category.groups.find(
    alert => alert.externalId === externalId
  ) as ChecksAlert | undefined;
  if (!alert) return state;

  const filtered = category.groups.filter(
    alert => alert.externalId !== action.externalId
  );
  alert.checks = alert.checks.map(check =>
    check.characteristic === characteristic
      ? {
          ...check,
          result: moveResult(check.result),
        }
      : check
  );

  return {
    ...state,
    currentPage: {
      ...state.currentPage,
      [state.selectedCategory]: {
        ...category,
        groups: [...filtered, { ...alert }],
      },
    },
  };
}

function handleUpdateSSIUnitTextUsed(
  state: AssessmentPageReducerType,
  action: IUpdateSSIUnitTextUsed
) {
  const { externalId, text } = action;

  if (!state.currentPage) return state;
  const category = state.currentPage[state.selectedCategory];
  const alert = category.groups.find(
    alert => alert.externalId === externalId
  ) as ChecksAlert | undefined;
  if (!alert) return state;

  const filtered = category.groups.filter(
    alert => alert.externalId !== action.externalId
  );
  alert.checks = alert.checks.map(check =>
    check.characteristic === ReviewCharacteristic.ssiCheck
      ? {
          ...check,
          usedValues: [
            {
              [SuggestionValueKeys.ssiCheck]: text,
            },
          ],
        }
      : check
  );

  return {
    ...state,
    currentPage: {
      ...state.currentPage,
      [state.selectedCategory]: {
        ...category,
        groups: [...filtered, { ...alert }],
      },
    },
  };
}

function handleUpdateSSIUnitTextRequired(
  state: AssessmentPageReducerType,
  action: IUpdateSSIUnitTextRequired
) {
  const { externalId, text } = action;

  if (!state.currentPage) return state;
  const category = state.currentPage[state.selectedCategory];
  const alert = category.groups.find(
    alert => alert.externalId === externalId
  ) as ChecksAlert | undefined;
  if (!alert) return state;

  const filtered = category.groups.filter(
    alert => alert.externalId !== action.externalId
  );
  alert.checks = alert.checks.map(check =>
    check.characteristic === ReviewCharacteristic.ssiCheck
      ? {
          ...check,
          requiredValues: [
            {
              [SuggestionValueKeys.ssiCheck]: text,
            },
          ],
        }
      : check
  );

  return {
    ...state,
    currentPage: {
      ...state.currentPage,
      [state.selectedCategory]: {
        ...category,
        groups: [...filtered, { ...alert }],
      },
    },
  };
}

export default function AssessmentPageReducer(
  state = initialState(),
  action: AnyAction
) {
  // console.log(state, action);

  switch (action.type) {
    case FETCH_ASSESSMENT_PAGE_START:
      return handleFetchStart(state);
    case FETCH_ASSESSMENT_PAGE_SUCCESS:
      return handleFetchSuccess(state, action as IFetchAssessmentPageSuccess);
    case FETCH_ASSESSMENT_PAGE_ERROR:
      return handleFetchError(state, action as IFetchAssessmentPageError);
    case PAGE_CHANGE:
      return handlePageChange(state, action as IPageChange);
    case SCALE_CHANGE:
      return handleScaleChange(state, action as IScaleChange);
    case CHECK_CATEGORY_CHANGE:
      return handleCheckCategoryChange(state, action as ICheckCategoryChange);
    case CHECK_SELECTION:
      return handleCheckSelection(state, action as ICheckSelection);
    case SET_EXTERNAL_ID:
      return handleSetExternalId(state, action as IExternalId);
    case SET_PREV_EXTERNAL_ID:
      return handleSetPrevExternalId(state, action as IExternalId);
    case SET_QUALITY_CHECK_TABNAME:
      return handleTabNameSelection(state, action as IChangeTabName);
    case TOGGLE_BORDERS_VISIBILITY:
      return handleToggleBordersVisibility(
        state,
        action as IToggleBordersVisibility
      );
    case SET_UNIT_BOX_COORDINATES:
      return handleSetUnitBoxCoordinates(
        state,
        action as ISetUnitBoxCoordinates
      );
    case REMOVE_SSI_UNIT:
      return handleRemoveSSIUnit(state, action as IRemoveSSIUnit);
    case ADD_SSI_UNIT:
      return handleAddSSIUnit(state, action as IAddSSIUnit);
    case UPDATE_SSI_UNIT_RESULT:
      return handleUpdateSSIUnitResult(state, action as IUpdateSSIUnitResult);
    case UPDATE_SSI_UNIT_TEXT_USED:
      return handleUpdateSSIUnitTextUsed(
        state,
        action as IUpdateSSIUnitTextUsed
      );
    case UPDATE_SSI_UNIT_TEXT_REQUIRED:
      return handleUpdateSSIUnitTextRequired(
        state,
        action as IUpdateSSIUnitTextRequired
      );
    default:
      return state;
  }
}
